import useBackendClient from "@/client";
import useToast from "@/hooks/useToastNotification";
import { AxiosError } from "axios";
import { computed, Ref, ref } from "vue";
import { Reward, RewardInput } from "../types";
import { getRewards } from "./useRewardsApi";

const { toastSuccess, toastError } = useToast();

export default function useRewards(appId: string, versionId: string) {
  const { client } = useBackendClient();
  const rewards: Ref<Reward[]> = ref([]);
  const queryLoading = ref(false);

  const isWIP = computed(() => versionId === "wip");

  const fetchRewards = async () => {
    queryLoading.value = true;

    try {
      const rewardsData = await getRewards(appId, versionId);
      rewards.value = rewardsData;
    } catch (error) {
      console.error("Failed to fetch rewards.", error);
    } finally {
      queryLoading.value = false;
    }
  };

  const rewardsToDisplay = computed(() => rewards.value);

  const addReward = async (input: Reward) => {
    if (!isWIP.value) {
      toastError({
        detail: "You cannot add rewards on non WIP version.",
        life: 3 * 1000,
        summary: "Error",
      });
      return;
    }
    try {
      await client.post(`/app/${appId}/version/${versionId}/rewards`, input);
      await fetchRewards();

      toastSuccess({
        detail: "Reward created successfully.",
        life: 3 * 1000,
        summary: "Success",
      });
    } catch (error) {
      toastError({
        detail: "Failed to add reward, please try again.",
        life: 3 * 1000,
        summary: "Error",
      });
      console.error("Failed to create reward. Error:", error);
    }
  };

  const editReward = async (rewardId: string, input: RewardInput) => {
    if (!isWIP.value) {
      toastError({
        detail: "You cannot edit rewards on non WIP version.",
        life: 3 * 1000,
        summary: "Error",
      });
      return;
    }
    try {
      await client.put(`/app/${appId}/version/${versionId}/rewards/${rewardId}`, input);
      await fetchRewards();

      toastSuccess({
        detail: "Reward updated successfully.",
        life: 3 * 1000,
        summary: "Success",
      });
    } catch (error) {
      console.error("Failed to update reward. Error:", error);
    }
  };

  const deleteReward = async (rewardId: string) => {
    if (!isWIP.value) {
      toastError({
        detail: "You cannot delete rewards on non WIP version.",
        life: 3 * 1000,
        summary: "Error",
      });
      return;
    }
    try {
      await client.delete(`/app/${appId}/version/${versionId}/rewards/`, {
        data: {
          rewardIds: [rewardId],
        },
      });
      await fetchRewards();

      toastSuccess({
        detail: "Reward deleted successfully.",
        life: 3 * 1000,
        summary: "Success",
      });
    } catch (error) {
      console.error("Failed to update reward. Error:", error);
    }
  };

  const duplicateReward = async (rewardId: string, newName: string) => {
    try {
      await client.post(`/app/${appId}/version/${versionId}/rewards/${rewardId}/duplicate`, {
        newName,
      });
      await fetchRewards();

      toastSuccess({
        detail: "Reward duplicated successfully.",
        life: 3 * 1000,
        summary: "Success",
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        toastError({
          detail: error.response?.data.details || "An error occurred while duplicating the reward",
          summary: "Error",
        });
      }
    }
  };

  return {
    rewardsToDisplay,
    queryLoading,
    fetchRewards,
    addReward,
    addRewardLoading: queryLoading,
    editReward,
    updateRewardLoading: queryLoading,
    duplicateReward,
    deleteReward,
  };
}
